import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useUpsertOffer } from '~/pages/ApplicationDetails/data/useUpsertOffer';
import { usePatchApplication } from '~/data/useApplication';
import {
  useLetterPreview,
  LetterPreviewType,
  PreOfferLetterPreview,
  WaitlistLetterPreview,
  FinalOfferLetterPreview,
} from '~/pages/ApplicationDetails/data/useLetterPreview';
import { AdditionalDetailsType } from '.';
import moment from 'moment';
import { OfferType } from './types';

const getApplicationStateAndLetterPreviewType = (
  offerType: OfferType
): {
  targetApplicationState: 'INITIAL_OFFER' | 'FINAL_OFFER_ISSUED' | 'WAITLISTED';
  letterPreviewType: LetterPreviewType;
} => {
  switch (offerType) {
    case OfferType.PRE_OFFER:
      return {
        targetApplicationState: 'INITIAL_OFFER',
        letterPreviewType: LetterPreviewType.PRE_OFFER_LETTER_PREVIEW,
      };
    case OfferType.FINAL_OFFER:
      return {
        targetApplicationState: 'FINAL_OFFER_ISSUED',
        letterPreviewType: LetterPreviewType.FINAL_OFFER_LETTER_PREVIEW,
      };
    case OfferType.WAITLIST:
      return {
        targetApplicationState: 'WAITLISTED',
        letterPreviewType: LetterPreviewType.WAITLIST_LETTER_PREVIEW,
      };
    default:
      throw new Error('Invalid offer type');
  }
};

export function useIssueOfferActivity(applicationId: string, offerType: OfferType) {
  const { targetApplicationState, letterPreviewType } = getApplicationStateAndLetterPreviewType(offerType);
  const { apiError } = useErrorHandler();
  const { patchApplication } = usePatchApplication(applicationId);
  const { generateLetterPreview } = useLetterPreview(applicationId, letterPreviewType);
  const { upsertOffer } = useUpsertOffer(applicationId);

  async function generateOffer(props: {
    conditions: Array<string>;
    customCondition: string;
    deferralRequestId?: string;
    additionalNotes?: string;
    conditionDeadline?: string;
    additionalDetails?: AdditionalDetailsType;
  }): Promise<PreOfferLetterPreview | WaitlistLetterPreview | FinalOfferLetterPreview> {
    return await generateLetterPreview({
      conditions: props.conditions,
      customCondition: props.customCondition,
      deferralRequestId: props.deferralRequestId,
      additionalNotes: props.additionalNotes || props.additionalDetails?.additionalNotes || undefined,
      conditionDeadline:
        props.conditionDeadline !== undefined ? moment(props.conditionDeadline).format('YYYY-MM-DD') : undefined,
      firstPaymentFee: props.additionalDetails?.firstPaymentFee?.toString() ?? undefined,
      firstPaymentDate:
        props.additionalDetails?.firstPaymentDate !== undefined && props.additionalDetails?.firstPaymentDate !== ''
          ? moment(props.additionalDetails.firstPaymentDate).format('YYYY-MM-DD')
          : undefined,
      secondPaymentFee: props.additionalDetails?.secondPaymentFee?.toString() ?? undefined,
      secondPaymentDate:
        props.additionalDetails?.secondPaymentDate !== undefined && props.additionalDetails?.secondPaymentDate !== ''
          ? moment(props.additionalDetails.secondPaymentDate).format('YYYY-MM-DD')
          : undefined,
      tuitionFee: props.additionalDetails?.tuitionFee?.toString() ?? undefined,
      ancillaryFee: props.additionalDetails?.ancillaryFee?.toString() ?? undefined,
      termStartDate:
        props.additionalDetails?.termStartDate !== undefined && props.additionalDetails?.termStartDate !== ''
          ? moment(props.additionalDetails.termStartDate).format('YYYY-MM-DD')
          : undefined,
      firstDayOfClassOn:
        props.additionalDetails?.firstDayOfClassOn !== undefined && props.additionalDetails?.firstDayOfClassOn !== ''
          ? moment(props.additionalDetails.firstDayOfClassOn).format('YYYY-MM-DD')
          : undefined,
      lastDayOfClassOn:
        props.additionalDetails?.lastDayOfClassOn !== undefined && props.additionalDetails?.lastDayOfClassOn !== ''
          ? moment(props.additionalDetails.lastDayOfClassOn).format('YYYY-MM-DD')
          : undefined,
    } as Parameters<typeof generateLetterPreview>[0]).catch((err) => {
      apiError();
      throw err;
    });
  }

  async function issueOffer(
    conditions: Array<string>,
    customCondition: string,
    offerType: OfferType,
    additionalDetails?: AdditionalDetailsType
  ): Promise<boolean> {
    await upsertOffer({
      firstPaymentFee: additionalDetails?.firstPaymentFee ? Number(additionalDetails.firstPaymentFee) : undefined,
      firstPaymentDate:
        additionalDetails?.firstPaymentDate !== undefined && additionalDetails?.firstPaymentDate !== ''
          ? moment(additionalDetails.firstPaymentDate).format('YYYY-MM-DD')
          : undefined,
      secondPaymentFee: additionalDetails?.secondPaymentFee ? Number(additionalDetails.secondPaymentFee) : undefined,
      secondPaymentDate:
        additionalDetails?.secondPaymentDate !== undefined && additionalDetails?.secondPaymentDate !== ''
          ? moment(additionalDetails.secondPaymentDate).format('YYYY-MM-DD')
          : undefined,
      tuitionFee: additionalDetails?.tuitionFee ? Number(additionalDetails.tuitionFee) : undefined,
      ancillaryFee: additionalDetails?.ancillaryFee ? Number(additionalDetails.ancillaryFee) : undefined,
      additionalNotes: additionalDetails?.additionalNotes || undefined,
      termStartDate:
        additionalDetails?.termStartDate !== undefined && additionalDetails?.termStartDate !== ''
          ? moment(additionalDetails.termStartDate).format('YYYY-MM-DD')
          : undefined,
      firstDayOfClassOn:
        additionalDetails?.firstDayOfClassOn !== undefined && additionalDetails?.firstDayOfClassOn !== ''
          ? moment(additionalDetails.firstDayOfClassOn).format('YYYY-MM-DD')
          : undefined,
      lastDayOfClassOn:
        additionalDetails?.lastDayOfClassOn !== undefined && additionalDetails?.lastDayOfClassOn !== ''
          ? moment(additionalDetails.lastDayOfClassOn).format('YYYY-MM-DD')
          : undefined,
    });
    const response = await patchApplication({
      status: targetApplicationState,
      conditions,
      customCondition,
    });
    // TODO: need to check
    const hasInitialOfferActions = response.status.actionGroups[0].actions.length > 0;
    return hasInitialOfferActions;
  }
  return {
    generateOffer,
    issueOffer,
  };
}
